import { useContext } from "react";
import { t } from "i18next";
import { TbLink, TbTrash } from "react-icons/tb";
import { Button, Input, Switcher, Tooltip } from "components/ui";
import capitalize from "components/ui/utils/capitalize";
import { CompanyContext } from "contexts/CompanyContext";
import MagicEditor from "components/layout/MagicEditor";
import MySocialIcon from "components/shared/MySocialIcon";
import BoardDropdown from "components/posts_previews/components/BoardDropdown";
import PlaylistDropdown from "components/posts_previews/components/PlaylistDropdown";
import CategoryDropdown from "components/posts_previews/components/CategoryDropdown";
import TagsInput from "components/posts_previews/components/TagsInput";
import TiktokPrivacyLevelDropdown from "components/posts_previews/components/TiktokPrivacyLevelDropdown";
import YoutubePrivacyLevelDropdown from "components/posts_previews/components/YoutubePrivacyLevelDropdown";
import usePostValidator, { getNetworkValidationIndex } from "utils/hooks/usePostValidator";
import { allowedCommentsNetworks } from "utils/comments";
import isMac from "utils/isMac";
import ComposerField, { ComposerCollapse } from "./ComposerField";
import ComposerMediaField from "./ComposerMediaField";
import ComposerCommentsField from "./ComposerCommentsField";

const TIKTOK_TOGGLES = [
    { key: "disable_comment", integrationKey: "comment_disabled", label: "Disable comments" },
    { key: "disable_duet", integrationKey: "duet_disabled", label: "Disable Duet" },
    { key: "disable_stitch", integrationKey: "stitch_disabled", label: "Disable Stitch" }
];

// Frame for the network option dropdowns (they render their own "!p-4
// border-b" rows, designed for the preview header strip).
const optionBoxClassName = "rounded-lg ring-1 ring-gray-300 dark:ring-gray-600 overflow-hidden bg-secondary-500 dark:bg-gray-800 [zoom:0.9]";

/**
 * Every editable field for ONE post of the group, driven by the network's
 * validation constants. For thread networks (twitter/threads/bluesky) and
 * stories this renders once per item, with a numbered header and a delete
 * action.
 */
const ComposerPostFields = ({
    post,
    network,
    isEditable = true,
    index = 0,
    count = 1,
    itemNoun = "Post",
    allPosts = [],
    onTextEdit,
    onTitleEdit,
    onLinkEdit,
    onAssetNew,
    onAssetDelete,
    onAssetEdit,
    onCoverEdit,
    onPreferencesEdit,
    onCommentsToCreateEdit,
    onDeleteIndexPost
}) => {

    const { companySocials } = useContext(CompanyContext);
    const { validationConstants } = usePostValidator();

    const mediaProductType = post?.preferences?.media_product_type || "FEED";
    const validationIndex = getNetworkValidationIndex(network, mediaProductType);
    const constants = validationConstants?.[validationIndex];

    const isStory = mediaProductType == "STORY";
    const showTitle = !!constants?.title?.required || network === "youtube";
    // Stories carry no caption: onSave blanks the description for STORY posts
    const showCaption = !isStory;
    const showLink = network === "pinterest";
    const showMedia = network !== "medium";
    const showComments = allowedCommentsNetworks.includes(network) && !isStory;

    const tiktokIntegration = network === "tiktok" ? companySocials?.find(e => e.type == "tiktok") : undefined;
    const youtubeIntegration = network === "youtube" ? companySocials?.find(e => e.type == "youtube") : undefined;

    const handlePreferenceChange = (key, value) => {
        onPreferencesEdit(post.postIndex, { ...(post?.preferences || {}), [key]: value });
    };

    return (
        <div className="flex flex-col gap-5">
            {count > 1 && (
                <div className="flex items-center justify-between -mb-1">
                    <span className="text-xs font-bold uppercase tracking-wider text-tertiary-400 dark:text-gray-500">
                        {itemNoun} {index + 1}
                    </span>
                    {isEditable && (
                        <Tooltip title={t('composer.deleteItem', { defaultValue: 'Delete this {{item}}', item: itemNoun.toLowerCase() })}>
                            <Button
                                size="xs"
                                variant="plain"
                                className="!rounded-lg hover:!text-red-600"
                                icon={<TbTrash />}
                                onClick={() => onDeleteIndexPost(post.postIndex)}
                            />
                        </Tooltip>
                    )}
                </div>
            )}

            {showMedia && (
                <ComposerMediaField
                    post={post}
                    network={network}
                    isEditable={isEditable}
                    allPosts={allPosts}
                    constants={constants}
                    onAssetNew={onAssetNew}
                    onAssetDelete={onAssetDelete}
                    onAssetEdit={onAssetEdit}
                    onPreferencesEdit={onPreferencesEdit}
                    onCoverEdit={onCoverEdit}
                />
            )}

            {network === "pinterest" && (
                <ComposerField label={t('composer.boardLabel', 'Board')} required>
                    <div className={optionBoxClassName}>
                        <BoardDropdown
                            post={post}
                            isEditable={isEditable}
                            portal={true}
                            onChange={(board_id) => handlePreferenceChange("board_id", board_id)}
                        />
                    </div>
                </ComposerField>
            )}

            {showTitle && (
                <ComposerField
                    label={t('composer.titleLabel', 'Title')}
                    required={!!constants?.title?.required}
                >
                    <div className="rounded-lg ring-1 ring-gray-300 dark:ring-gray-600 focus-within:ring-primary-600 transition-shadow bg-secondary-500 dark:bg-gray-800 overflow-hidden pb-1">
                        <MagicEditor
                            value={post?.title}
                            setValue={(value) => onTitleEdit(post.postIndex, value)}
                            network={network}
                            mediaProductType={mediaProductType}
                            post={post}
                            isTitle={true}
                            rows={1}
                            minHeight={20}
                            placeholder={t('composer.titlePlaceholder', 'Write a title...')}
                            className="!px-3 !py-2 w-full !text-base !font-semibold !bg-transparent"
                            optionsBarClassName="!mt-0 !px-3"
                            readOnly={!isEditable}
                        />
                    </div>
                </ComposerField>
            )}

            {showCaption && (
                <ComposerField
                    label={network === "medium"
                        ? t('composer.bodyLabel', 'Body')
                        : t('composer.captionLabel', 'Caption')
                    }
                    required={!!constants?.description?.required}
                >
                    <div className="rounded-lg ring-1 ring-gray-300 dark:ring-gray-600 focus-within:ring-primary-600 transition-shadow bg-secondary-500 dark:bg-gray-800 overflow-hidden">
                        <MagicEditor
                            value={post?.text}
                            setValue={(value) => onTextEdit(post.postIndex, value)}
                            network={network}
                            mediaProductType={mediaProductType}
                            post={post}
                            minHeight={100}
                            placeholder={t('composer.captionPlaceholder', 'Write your post...')}
                            className="!px-3 !py-2 w-full !text-[15px] !bg-transparent !min-h-[9rem]"
                            optionsBarClassName="!mt-0 !px-3"
                            readOnly={!isEditable}
                        />
                        {isEditable && (
                            <div className="mt-1.5 px-3 pt-1.5 pb-1.5 border-t border-gray-100 dark:border-gray-700 flex items-center gap-1.5 text-[11px] text-tertiary-400/70 dark:text-gray-500/60 select-none">
                                <span className="button-key">{isMac() ? `⌘ + Shift + E` : `Ctrl + Shift + E`}</span>
                                <span className="opacity-70">{t('composer.aiHint', 'or right-click in the text to open AI writing tools.')}</span>
                            </div>
                        )}
                    </div>
                </ComposerField>
            )}

            {showLink && (
                <ComposerField label={t('composer.linkLabel', 'Destination link')}>
                    <Input
                        size="sm"
                        className="!rounded-lg"
                        prefix={<TbLink className="text-lg text-tertiary-400" />}
                        value={post?.link || ""}
                        disabled={!isEditable}
                        placeholder="https://"
                        onChange={(e) => onLinkEdit(post.postIndex, e.target.value)}
                    />
                </ComposerField>
            )}

            {network === "tiktok" && (
                <>
                    <ComposerField label={t('composer.privacyLabel', 'Who can view this video')} required>
                        <div className={optionBoxClassName}>
                            <TiktokPrivacyLevelDropdown
                                post={post}
                                isEditable={isEditable}
                                portal={true}
                                onChange={(privacy_level) => handlePreferenceChange("privacy_level", privacy_level)}
                            />
                        </div>
                    </ComposerField>
                    <ComposerCollapse
                        title={t('composer.networkOptions', { defaultValue: '{{network}} options', network: capitalize(network) })}
                        icon={<MySocialIcon network={network} className="!w-4 !h-4" wrapperClassName="!w-4 !h-4" />}
                    >
                        {TIKTOK_TOGGLES.map(({ key, integrationKey, label }) => {
                            const lockedByIntegration = !!tiktokIntegration?.preferences?.[integrationKey];
                            return (
                                <Tooltip
                                    key={key}
                                    disabled={!lockedByIntegration}
                                    title={`You must change your TikTok's user privacy settings to change this setting.`}
                                    wrapperClass={`${lockedByIntegration && `!cursor-not-allowed opacity-[0.5]`}`}
                                >
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="text-sm text-tertiary-700 dark:text-gray-300">{label}</span>
                                        <Switcher
                                            className="zoom-70"
                                            checked={!!post?.preferences?.[key]}
                                            disabled={!isEditable || lockedByIntegration}
                                            onChange={() => handlePreferenceChange(key, post?.preferences?.[key] ? 0 : 1)}
                                        />
                                    </div>
                                </Tooltip>
                            );
                        })}
                    </ComposerCollapse>
                </>
            )}

            {network === "youtube" && !!youtubeIntegration && (
                <ComposerCollapse
                    title={t('composer.networkOptions', { defaultValue: '{{network}} options', network: capitalize(network) })}
                    icon={<MySocialIcon network={network} className="!w-4 !h-4" wrapperClassName="!w-4 !h-4" />}
                >
                    <ComposerField label={t('composer.youtubePrivacyLabel', 'Visibility')}>
                        <div className={optionBoxClassName}>
                            <YoutubePrivacyLevelDropdown
                                post={post}
                                isEditable={isEditable}
                                portal={true}
                                onChange={(privacy_level) => handlePreferenceChange("privacy_level", privacy_level)}
                            />
                        </div>
                    </ComposerField>
                    <ComposerField
                        label={t('composer.youtubeTagsLabel', 'Tags')}
                        hint={t('composer.youtubeTagsHelp', 'Press Enter after each tag to add it.')}
                    >
                        <TagsInput
                            post={post}
                            isEditable={isEditable}
                            placeholder={t('composer.youtubeTagsPlaceholder', 'Add YouTube tags...')}
                            className="!rounded-lg ring-1 ring-gray-300 dark:ring-gray-600 !px-3 !py-2"
                            onChange={(tags) => handlePreferenceChange("tags", tags)}
                        />
                    </ComposerField>
                    <ComposerField label={t('composer.youtubeCategoryLabel', 'Category')}>
                        <div className={optionBoxClassName}>
                            <CategoryDropdown
                                post={post}
                                isEditable={isEditable}
                                portal={true}
                                onChange={(category_id) => handlePreferenceChange("category_id", category_id)}
                            />
                        </div>
                    </ComposerField>
                    <ComposerField label={t('composer.youtubePlaylistLabel', 'Playlist')}>
                        <div className={optionBoxClassName}>
                            <PlaylistDropdown
                                post={post}
                                isEditable={isEditable}
                                portal={true}
                                onChange={(playlist_id) => handlePreferenceChange("playlist_id", playlist_id)}
                            />
                        </div>
                    </ComposerField>
                </ComposerCollapse>
            )}

            {showComments && (
                <ComposerCommentsField
                    post={post}
                    network={network}
                    isEditable={isEditable}
                    onCommentsToCreateEdit={onCommentsToCreateEdit}
                />
            )}
        </div>
    );
};

export default ComposerPostFields;
